import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Globe, Briefcase, User, Mail, 
  Plus, Trash, Save, Lock, LogOut, X, Tag, BarChart as ChartIcon, Share2, Layers, Zap, Building, Loader2, ArrowUp, ArrowDown
} from 'lucide-react';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { GlassCard, GlassButton, SectionTitle } from '../components/LiquidComponents';
import { useData } from '../contexts/DataContext';
import { Project, UserProfile, SiteConfig, InboxMessage, Experience, Education } from '../types';
import { PROJECT_STAGES } from '../constants/stages';
import { resolveProjectSlug } from '../utils/projectSlug';
import { supabase } from '../src/lib/supabase';
import type { Session } from '@supabase/supabase-js';

// --- КОМПОНЕНТЫ ВЫНЕСЕНЫ НАРУЖУ ---

const SidebarItem = ({ view, icon: Icon, label, active, onClick }: any) => (
  <button onClick={onClick} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${active ? 'bg-blue-600 text-white shadow-lg' : 'text-zinc-500 hover:text-white hover:bg-white/5'}`}>
    <Icon size={18} /> <span className="text-sm font-medium">{label}</span>
  </button>
);

const InputField = ({ label, value, onChange, placeholder, className = '' }: any) => (
  <div className={className}>
    <label className="block text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1.5">{label}</label>
    <input 
      type="text" 
      value={value || ''} 
      onChange={onChange} 
      className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white text-sm focus:border-blue-500 outline-none transition-colors" 
      placeholder={placeholder} 
    />
  </div>
);

const TextAreaField = ({ label, value, onChange, placeholder, rows = 4 }: any) => (
  <div>
    <label className="block text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1.5">{label}</label>
    <textarea 
      rows={rows} 
      value={value || ''} 
      onChange={onChange} 
      className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white text-sm focus:border-blue-500 outline-none resize-none transition-colors" 
      placeholder={placeholder} 
    />
  </div>
);

// --- MAIN ADMIN ---

export const Admin: React.FC = () => {
  const { projects: globalProjects, profile: globalProfile, siteConfig: globalConfig, inbox: globalMessages, loading, updateProjects, updateProfile, updateSiteConfig, markMessageRead } = useData();

  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [isSaving, setIsSaving] = useState(false);
  
  // Local Data
  const [projects, setProjects] = useState<Project[]>([]);
  const [profile, setProfile] = useState<UserProfile>({
    name: '', nickname: '', email: '', location: '', bio: '', avatarUrl: '', bookingUrl: '',
    socials: {}, skills: [], experience: [], education: [], investmentCard: { title: '', body: '', buttonText: '' }
  });
  const [config, setConfig] = useState<SiteConfig>({
    maintenanceMode: false, welcomeMessage: '', footerText: '', heroHeadline: '', heroSubheadline: '', heroCtaText: '', metaTitle: ''
  });
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  
  // Editing
  const [editingProject, setEditingProject] = useState<Partial<Project> | null>(null);
  const [metricLabel, setMetricLabel] = useState('');
  const [metricValue, setMetricValue] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [desktopShowcaseInput, setDesktopShowcaseInput] = useState('');
  const [mobileShowcaseInput, setMobileShowcaseInput] = useState('');
  const [skillInput, setSkillInput] = useState('');

  // Sync
  useEffect(() => {
    if (!loading) {
      setProjects(globalProjects);
      if (globalProfile) setProfile(prev => ({ ...prev, ...globalProfile }));
      if (globalConfig) setConfig(prev => ({ ...prev, ...globalConfig }));
      setMessages(globalMessages);
    }
  }, [loading, globalProjects, globalProfile, globalConfig, globalMessages]);

  // Supabase Auth: restore existing session on mount and keep it in sync
  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setAuthLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setSigningIn(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setAuthError('Invalid email or password');
        setPassword('');
      } else {
        // onAuthStateChange will pick up the new session and reveal the panel
        setPassword('');
      }
    } catch {
      setAuthError('Something went wrong. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    // onAuthStateChange will clear the session and return to the login form
  };

  // --- SAVE HANDLERS ---

  const handleSaveGlobal = async () => {
    setIsSaving(true);
    try { await updateSiteConfig(config); alert('Saved!'); } finally { setIsSaving(false); }
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try { await updateProfile(profile); alert('Saved!'); } finally { setIsSaving(false); }
  };

  const handleSaveProject = async () => {
    setIsSaving(true);
    try {
      const projName = editingProject?.title || editingProject?.name;
      if (editingProject && projName) {
        const existingSortOrder = projects.find(p => p.id === editingProject.id)?.sortOrder;
        const resolvedSortOrder = editingProject.sortOrder ?? existingSortOrder ?? projects.length;
        const crunchbaseUrl = editingProject.crunchbase?.trim();
        const crunchbaseText = crunchbaseUrl ? (editingProject.crunchbaseText?.trim() || 'View on Crunchbase') : null;
        const defaultShowcaseView = editingProject.defaultShowcaseView || 'desktop';
        const newProject = {
          ...editingProject,
          id: editingProject.id || crypto.randomUUID(),
          slug: resolveProjectSlug(editingProject),
          title: projName,
          tags: editingProject.tags || [],
          metrics: editingProject.metrics || [],
          socials: editingProject.socials || {},
          stage: editingProject.stage || 'Concept',
          description: editingProject.description || '',
          // --- ИСПРАВЛЕНИЕ: Добавляем поле content (Deep Dive) ---
          content: editingProject.content || '', 
          // -----------------------------------------------------
          imageUrl: editingProject.imageUrl || '',
          logoUrl: editingProject.logoUrl || '',
          websiteUrl: editingProject.websiteUrl || '',
          crunchbase: crunchbaseUrl || null,
          crunchbaseText,
          subtitle: editingProject.subtitle || '',
          desktopShowcaseImages: editingProject.desktopShowcaseImages && editingProject.desktopShowcaseImages.length > 0 ? editingProject.desktopShowcaseImages : null,
          mobileShowcaseImages: editingProject.mobileShowcaseImages && editingProject.mobileShowcaseImages.length > 0 ? editingProject.mobileShowcaseImages : null,
          defaultShowcaseView,
          sortOrder: resolvedSortOrder
        } as Project;

        let updatedProjects;
        if (projects.find(p => p.id === newProject.id)) {
           updatedProjects = projects.map(p => p.id === newProject.id ? newProject : p);
        } else {
           updatedProjects = [...projects, newProject];
        }

        setProjects(updatedProjects);
        await updateProjects(updatedProjects);
        
        setEditingProject(null);
        setMetricLabel(''); setMetricValue(''); setTagInput(''); setDesktopShowcaseInput(''); setMobileShowcaseInput('');
      }
    } catch (error: any) {
      console.error(error);
      alert(`Error saving project: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProject = async (id: string) => {
    if (confirm('Delete project?')) {
      const updated = projects.filter(p => p.id !== id);
      setProjects(updated);
      await updateProjects(updated);
    }
  };

  // --- HELPERS FOR PROFILE LISTS ---

  const addExperience = () => {
    const newExp: Experience = { company: 'New Company', role: 'Role', period: '2024', description: '', location: '' };
    setProfile({ ...profile, experience: [...profile.experience, newExp] });
  };

  const removeExperience = (idx: number) => {
    const newExp = [...profile.experience];
    newExp.splice(idx, 1);
    setProfile({ ...profile, experience: newExp });
  };

  const updateExperience = (idx: number, field: any, val: string) => {
    const newExp = [...profile.experience];
    newExp[idx] = { ...newExp[idx], [field]: val };
    setProfile({ ...profile, experience: newExp });
  };

  const addEducation = () => {
    const newEdu: Education = { school: 'University', degree: 'Degree', fieldOfStudy: '', grade: '', year: '2024', location: '' };
    setProfile({ ...profile, education: [...profile.education, newEdu] });
  };

  const removeEducation = (idx: number) => {
    const newEdu = [...profile.education];
    newEdu.splice(idx, 1);
    setProfile({ ...profile, education: newEdu });
  };

  const updateEducation = (idx: number, field: any, val: string) => {
    const newEdu = [...profile.education];
    newEdu[idx] = { ...newEdu[idx], [field]: val };
    setProfile({ ...profile, education: newEdu });
  };

  // --- HELPERS FOR PROJECT ---

  const handleAddMetric = (e: React.MouseEvent) => {
    e.preventDefault();
    if (editingProject && metricLabel && metricValue) {
      setEditingProject({...editingProject, metrics: [...(editingProject.metrics||[]), {label: metricLabel, value: metricValue}]});
      setMetricLabel(''); setMetricValue('');
    }
  };

  const handleRemoveMetric = (idx: number) => {
    if (editingProject?.metrics) {
      const newMetrics = [...editingProject.metrics];
      newMetrics.splice(idx, 1);
      setEditingProject({ ...editingProject, metrics: newMetrics });
    }
  };

  const handleAddTag = (e: React.MouseEvent) => {
    e.preventDefault();
    if (editingProject && tagInput) {
       setEditingProject({...editingProject, tags: [...(editingProject.tags||[]), tagInput]});
       setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    if (editingProject?.tags) {
      setEditingProject({ ...editingProject, tags: editingProject.tags.filter(t => t !== tagToRemove) });
    }
  };

  const handleAddDesktopShowcaseImage = (e: React.MouseEvent) => {
    e.preventDefault();
    const nextUrl = desktopShowcaseInput.trim();
    if (editingProject && nextUrl) {
      setEditingProject({
        ...editingProject,
        desktopShowcaseImages: [...(editingProject.desktopShowcaseImages || []), nextUrl]
      });
      setDesktopShowcaseInput('');
    }
  };

  const handleRemoveDesktopShowcaseImage = (indexToRemove: number) => {
    if (editingProject?.desktopShowcaseImages) {
      setEditingProject({
        ...editingProject,
        desktopShowcaseImages: editingProject.desktopShowcaseImages.filter((_, idx) => idx !== indexToRemove)
      });
    }
  };

  const handleAddMobileShowcaseImage = (e: React.MouseEvent) => {
    e.preventDefault();
    const nextUrl = mobileShowcaseInput.trim();
    if (editingProject && nextUrl) {
      setEditingProject({
        ...editingProject,
        mobileShowcaseImages: [...(editingProject.mobileShowcaseImages || []), nextUrl]
      });
      setMobileShowcaseInput('');
    }
  };

  const handleRemoveMobileShowcaseImage = (indexToRemove: number) => {
    if (editingProject?.mobileShowcaseImages) {
      setEditingProject({
        ...editingProject,
        mobileShowcaseImages: editingProject.mobileShowcaseImages.filter((_, idx) => idx !== indexToRemove)
      });
    }
  };

  const handleAddSkill = (e: React.MouseEvent) => {
    e.preventDefault();
    if (profile && skillInput) {
      setProfile({ ...profile, skills: [...profile.skills, skillInput] });
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    if (profile) {
      setProfile({ ...profile, skills: profile.skills.filter(s => s !== skillToRemove) });
    }
  };

  const handleMoveProject = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= projects.length) return;

    const currentProject = projects[index];
    const targetProject = projects[newIndex];
    const currentOrder = currentProject.sortOrder ?? index;
    const targetOrder = targetProject.sortOrder ?? newIndex;

    const previous = [...projects];
    const reordered = [...projects];
    reordered[newIndex] = { ...currentProject, sortOrder: targetOrder };
    reordered[index] = { ...targetProject, sortOrder: currentOrder };

    setProjects(reordered);
    try {
      await updateProjects(reordered);
    } catch (error) {
      console.error('Failed to reorder projects', error);
      setProjects(previous);
      alert('Failed to update project order. Please try again.');
    }
  };

  const resolvedShowcaseView = editingProject?.defaultShowcaseView ?? 'desktop';

  if (authLoading) return (
    <div className="h-screen flex items-center justify-center p-4 pt-20">
      <Loader2 className="text-white animate-spin" size={28} />
    </div>
  );

  if (!session) return (
    <div className="h-screen flex items-center justify-center p-4 pt-20">
      <GlassCard className="p-10 w-full max-w-md text-center">
        <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6"><Lock className="text-white" /></div>
        <h2 className="text-2xl text-white mb-6">CMS Access</h2>
        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="email"
            placeholder="Email"
            autoComplete="email"
            required
            className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white text-center outline-none focus:border-blue-500 transition-colors"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setAuthError(''); }}
          />
          <input
            type="password"
            placeholder="Password"
            autoComplete="current-password"
            required
            className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white text-center outline-none focus:border-blue-500 transition-colors"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setAuthError(''); }}
          />
          {authError && <p className="text-red-400 text-sm">{authError}</p>}
          <GlassButton type="submit" disabled={signingIn} className="w-full justify-center">
            {signingIn ? <><Loader2 size={16} className="animate-spin" /> Signing in...</> : 'Sign In'}
          </GlassButton>
        </form>
      </GlassCard>
    </div>
  );

  return (
    <div className="min-h-screen pt-28 md:pt-40 pb-10 container mx-auto px-4 max-w-7xl flex gap-6">
      <div className="w-64 hidden lg:block shrink-0">
        <GlassCard className="h-full min-h-[600px] p-4 flex flex-col sticky top-32">
           <div className="px-4 py-4 mb-4 border-b border-white/5"><h3 className="text-xs font-bold text-zinc-500 uppercase">Admin Panel</h3></div>
           <nav className="space-y-2 flex-1">
             {['dashboard', 'global', 'portfolio', 'profile', 'inbox'].map(v => (
               <SidebarItem key={v} icon={v==='dashboard'?LayoutDashboard:v==='global'?Globe:v==='portfolio'?Briefcase:v==='profile'?User:Mail} label={v.charAt(0).toUpperCase()+v.slice(1)} active={activeView===v} onClick={()=>setActiveView(v as any)} />
             ))}
           </nav>
           <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl mt-auto"><LogOut size={18} /><span className="text-sm">Logout</span></button>
        </GlassCard>
      </div>

      <div className="flex-1 w-full overflow-hidden">
        <div className="lg:hidden mb-6 overflow-x-auto flex gap-2 pb-2">
           {['dashboard', 'global', 'portfolio', 'profile', 'inbox'].map((v) => (
             <button key={v} onClick={() => setActiveView(v as any)} className={`px-4 py-2 rounded-full text-xs font-medium uppercase ${activeView === v ? 'bg-blue-600 text-white' : 'bg-white/5 text-zinc-500'}`}>{v}</button>
           ))}
        </div>

        {activeView === 'dashboard' && (
          <div className="space-y-6 animate-in fade-in">
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <GlassCard className="p-6"><h4 className="text-zinc-500 text-xs uppercase mb-2">Projects</h4><div className="text-3xl text-white">{projects.length}</div></GlassCard>
                <GlassCard className="p-6"><h4 className="text-zinc-500 text-xs uppercase mb-2">Messages</h4><div className="text-3xl text-white">{messages.filter(m => !m.read).length}</div></GlassCard>
                <GlassCard className="p-6"><h4 className="text-zinc-500 text-xs uppercase mb-2">Views</h4><div className="text-3xl text-white">12.4k</div></GlassCard>
             </div>
             <GlassCard className="p-6 h-[300px]">
                <h4 className="text-white mb-6 flex items-center gap-2 text-sm"><ChartIcon size={16}/> Interest</h4>
                <ResponsiveContainer width="100%" height="80%">
                   <BarChart data={projects.map(p => ({ name: p.title?.substring(0,10), val: 10 }))}><Bar dataKey="val" fill="#3b82f6" /><XAxis dataKey="name" stroke="#888" /><Tooltip/></BarChart>
                </ResponsiveContainer>
             </GlassCard>
          </div>
        )}

        {activeView === 'global' && (
          <div className="space-y-6 animate-in fade-in">
             <SectionTitle title="Global Settings" />
             <GlassCard className="p-8 space-y-6">
                <InputField label="Hero Headline" value={config.heroHeadline} onChange={(e: any) => setConfig({...config, heroHeadline: e.target.value})} />
                <TextAreaField label="Hero Subheadline" value={config.heroSubheadline} onChange={(e: any) => setConfig({...config, heroSubheadline: e.target.value})} />
                <div className="grid grid-cols-2 gap-4">
                   <InputField label="CTA Button" value={config.heroCtaText} onChange={(e: any) => setConfig({...config, heroCtaText: e.target.value})} />
                   <InputField label="Meta Title" value={config.metaTitle} onChange={(e: any) => setConfig({...config, metaTitle: e.target.value})} />
                </div>
                <div className="flex justify-end pt-4"><GlassButton onClick={handleSaveGlobal} className="bg-green-600 hover:bg-green-500 border-none min-w-[120px]">{isSaving?<Loader2 className="animate-spin"/>:"Save Changes"}</GlassButton></div>
             </GlassCard>
          </div>
        )}

        {activeView === 'portfolio' && (
          <div className="space-y-6 animate-in fade-in">
             <div className="flex justify-between items-center"><SectionTitle title="Portfolio" /><GlassButton onClick={() => setEditingProject({ stage: 'Concept', tags: [], metrics: [], socials: {}, desktopShowcaseImages: [], mobileShowcaseImages: [], crunchbase: '', crunchbaseText: 'View on Crunchbase', defaultShowcaseView: 'desktop' } as any)}><Plus size={16}/> New Project</GlassButton></div>
             {editingProject && (
               <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                  <GlassCard className="w-full max-w-4xl max-h-[calc(100vh-120px)] flex flex-col p-0 overflow-y-auto bg-zinc-900 border-zinc-800">
                     <div className="p-6 border-b border-white/10 flex justify-between items-center bg-white/5 shrink-0"><h3 className="text-white text-lg">Edit Project</h3><button onClick={() => setEditingProject(null)}><X className="text-zinc-500 hover:text-white"/></button></div>
                     <div className="flex-1 p-8 space-y-8 custom-scrollbar">
                        <div className="grid grid-cols-2 gap-6">
                           <div className="space-y-4">
                              <InputField label="Name" value={editingProject.title || editingProject.name} onChange={(e: any) => setEditingProject({...editingProject, title: e.target.value})} />
                              <div className="grid grid-cols-2 gap-4">
                               <div className="space-y-1.5"><label className="text-[10px] text-zinc-500 uppercase font-semibold">Stage</label><select value={editingProject.stage} onChange={(e: any) => setEditingProject({...editingProject, stage: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white text-sm focus:border-white/40 outline-none">{PROJECT_STAGES.map(stage => (<option key={stage} value={stage}>{stage}</option>))}</select></div>
                                <InputField label="Role" value={editingProject.role} onChange={(e: any) => setEditingProject({...editingProject, role: e.target.value})} />
                              </div>
                               <InputField label="Logo URL" value={editingProject.logoUrl} onChange={(e: any) => setEditingProject({...editingProject, logoUrl: e.target.value})} />
                               <InputField label="Slug" value={editingProject.slug} onChange={(e: any) => setEditingProject({...editingProject, slug: e.target.value})} placeholder="e.g. sevenoir" />
                               <InputField label="Website" value={editingProject.websiteUrl} onChange={(e: any) => setEditingProject({...editingProject, websiteUrl: e.target.value})} />
                              <InputField label="Crunchbase URL" value={editingProject.crunchbase || ''} onChange={(e: any) => setEditingProject({...editingProject, crunchbase: e.target.value})} />
                              <InputField label="Crunchbase Link Display Text" value={editingProject.crunchbaseText ?? 'View on Crunchbase'} onChange={(e: any) => setEditingProject({...editingProject, crunchbaseText: e.target.value})} placeholder="View on Crunchbase" />
                              <div className="space-y-1.5">
                                <label className="text-[10px] text-zinc-500 uppercase font-semibold">Default Showcase View</label>
                                <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-1">
                                  <button
                                    type="button"
                                    onClick={() => setEditingProject({ ...editingProject, defaultShowcaseView: 'desktop' })}
                                    className={`flex-1 text-xs font-semibold uppercase tracking-wide px-3 py-2 rounded-md transition-all ${resolvedShowcaseView === 'desktop' ? 'bg-white/20 text-white shadow-sm' : 'text-zinc-400 hover:text-white'}`}
                                  >
                                    Desktop (16:9)
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditingProject({ ...editingProject, defaultShowcaseView: 'mobile' })}
                                    className={`flex-1 text-xs font-semibold uppercase tracking-wide px-3 py-2 rounded-md transition-all ${resolvedShowcaseView === 'mobile' ? 'bg-white/20 text-white shadow-sm' : 'text-zinc-400 hover:text-white'}`}
                                  >
                                    Mobile (9:16)
                                  </button>
                                </div>
                              </div>
                              <InputField label="Audio URL (mp3 link)" value={editingProject.audioUrl} onChange={(e: any) => setEditingProject({...editingProject, audioUrl: e.target.value})} />
                            </div>
                           
                           {/* Project Deep Dive Field */}
                           <div className="space-y-4">
                              <TextAreaField label="Description (Short)" rows={4} value={editingProject.description} onChange={(e: any) => setEditingProject({...editingProject, description: e.target.value})} />
                              <TextAreaField label="Project Deep Dive (Full Content)" rows={8} value={editingProject.content} onChange={(e: any) => setEditingProject({...editingProject, content: e.target.value})} />
                           </div>
                           
                        </div>
                        
                        <div className="pt-6 border-t border-white/5 space-y-4">
                           <h4 className="text-white text-sm font-medium flex gap-2"><ChartIcon size={16}/> Metrics</h4>
                           <div className="flex gap-2">
                             <input placeholder="Label" value={metricLabel} onChange={e => setMetricLabel(e.target.value)} className="w-1/3 bg-white/5 border border-white/10 rounded p-2 text-xs text-white" />
                             <input placeholder="Value" value={metricValue} onChange={e => setMetricValue(e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded p-2 text-xs text-white" />
                             <button onClick={handleAddMetric} className="bg-blue-600 text-white p-2 rounded"><Plus size={16}/></button>
                           </div>
                           <div className="space-y-2">
                             {editingProject.metrics?.map((m, i) => (
                               <div key={i} className="flex items-center gap-2 bg-white/5 p-2 rounded text-xs text-white border border-white/5">
                                 <span className="flex-1 text-zinc-300"><strong className="text-white">{m.label}:</strong> {m.value}</span>
                                 <button onClick={() => handleRemoveMetric(i)} className="text-zinc-500 hover:text-red-400 transition-colors"><X size={14}/></button>
                               </div>
                             ))}
                           </div>
                        </div>

                        <div className="pt-6 border-t border-white/5 space-y-4">
                           <h4 className="text-white text-sm font-medium flex gap-2"><Tag size={16}/> Tags</h4>
                           <div className="flex gap-2">
                             <input placeholder="Add tag..." value={tagInput} onChange={e => setTagInput(e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded p-2 text-xs text-white" />
                             <button onClick={handleAddTag} className="bg-white/10 text-white p-2 rounded"><Plus size={16}/></button>
                           </div>
                           <div className="flex flex-wrap gap-2">
                             {editingProject.tags?.map(tag => (
                               <span key={tag} className="flex items-center gap-1 bg-white/5 border border-white/10 px-2 py-1 rounded-md text-xs text-zinc-300">
                                 {tag} <button onClick={() => handleRemoveTag(tag)} className="hover:text-white transition-colors"><X size={12}/></button>
                               </span>
                             ))}
                           </div>
                        </div>

                        <div className="pt-6 border-t border-white/5 space-y-6">
                          <h4 className="text-white text-sm font-medium flex gap-2"><Layers size={16}/> Project Showcase / Gallery</h4>
                          <div className="space-y-3">
                            <h5 className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Desktop Showcase Images (16:9 Aspect Ratio)</h5>
                            <div className="flex gap-2">
                              <input placeholder="Desktop image URL..." value={desktopShowcaseInput} onChange={e => setDesktopShowcaseInput(e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded p-2 text-xs text-white" />
                              <button onClick={handleAddDesktopShowcaseImage} className="bg-white/10 text-white p-2 rounded"><Plus size={16}/></button>
                            </div>
                            <div className="space-y-2">
                              {editingProject.desktopShowcaseImages?.map((image, index) => (
                                <div key={`${image}-${index}`} className="flex items-center gap-2 bg-white/5 p-2 rounded text-xs text-white border border-white/5">
                                  <span className="flex-1 text-zinc-300 truncate">{image}</span>
                                  <button onClick={() => handleRemoveDesktopShowcaseImage(index)} className="text-zinc-500 hover:text-red-400 transition-colors"><X size={14}/></button>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-3">
                            <h5 className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Mobile Showcase Images (9:16 Aspect Ratio)</h5>
                            <div className="flex gap-2">
                              <input placeholder="Mobile image URL..." value={mobileShowcaseInput} onChange={e => setMobileShowcaseInput(e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded p-2 text-xs text-white" />
                              <button onClick={handleAddMobileShowcaseImage} className="bg-white/10 text-white p-2 rounded"><Plus size={16}/></button>
                            </div>
                            <div className="space-y-2">
                              {editingProject.mobileShowcaseImages?.map((image, index) => (
                                <div key={`${image}-${index}`} className="flex items-center gap-2 bg-white/5 p-2 rounded text-xs text-white border border-white/5">
                                  <span className="flex-1 text-zinc-300 truncate">{image}</span>
                                  <button onClick={() => handleRemoveMobileShowcaseImage(index)} className="text-zinc-500 hover:text-red-400 transition-colors"><X size={14}/></button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                         
                        {/* Socials */}
                        <div className="pt-6 border-t border-white/5 space-y-4">
                           <h4 className="text-white text-sm font-medium">Project Socials</h4>
                           <div className="grid grid-cols-2 gap-4">
                              <InputField label="LinkedIn" value={editingProject.socials?.linkedin} onChange={(e: any) => setEditingProject({...editingProject, socials: {...editingProject.socials, linkedin: e.target.value}})} />
                              <InputField label="X (Twitter)" value={editingProject.socials?.x} onChange={(e: any) => setEditingProject({...editingProject, socials: {...editingProject.socials, x: e.target.value}})} />
                              <InputField label="Instagram" value={editingProject.socials?.instagram} onChange={(e: any) => setEditingProject({...editingProject, socials: {...editingProject.socials, instagram: e.target.value}})} />
                              <InputField label="Telegram" value={editingProject.socials?.telegram} onChange={(e: any) => setEditingProject({...editingProject, socials: {...editingProject.socials, telegram: e.target.value}})} />
                              <InputField label="Reddit" value={editingProject.socials?.reddit} onChange={(e: any) => setEditingProject({...editingProject, socials: {...editingProject.socials, reddit: e.target.value}})} />
                           </div>
                        </div>
                     </div>
                     <div className="p-6 border-t border-white/10 bg-black/20 flex gap-3 justify-end shrink-0"><GlassButton onClick={handleSaveProject} className="bg-blue-600 border-none min-w-[120px]">{isSaving?<Loader2 className="animate-spin"/>:"Save Project"}</GlassButton></div>
                  </GlassCard>
               </div>
             )}
             <div className="grid gap-4">
               {projects.map((p, idx) => (
                 <div key={p.id} className="flex justify-between items-center p-4 bg-white/5 rounded-xl">
                   <div className="text-white">{p.title || p.name}</div>
                   <div className="flex gap-2">
                     <button
                       onClick={() => handleMoveProject(idx, 'up')}
                       disabled={idx === 0}
                       className="p-2 text-zinc-400 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                     >
                       <ArrowUp size={16} />
                     </button>
                     <button
                       onClick={() => handleMoveProject(idx, 'down')}
                       disabled={idx === projects.length - 1}
                       className="p-2 text-zinc-400 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                     >
                       <ArrowDown size={16} />
                     </button>
                     <button onClick={() => setEditingProject({ ...p, crunchbaseText: p.crunchbaseText || 'View on Crunchbase', defaultShowcaseView: p.defaultShowcaseView || 'desktop', desktopShowcaseImages: p.desktopShowcaseImages || [], mobileShowcaseImages: p.mobileShowcaseImages || [] })} className="p-2 text-blue-400 hover:text-white transition-colors">
                       <Globe size={16}/>
                     </button>
                     <button onClick={() => handleDeleteProject(p.id)} className="p-2 text-red-400 hover:text-white transition-colors">
                       <Trash size={16}/>
                     </button>
                   </div>
                 </div>
               ))}
             </div>
          </div>
        )}

        {activeView === 'profile' && profile && (
          <div className="space-y-6 animate-in fade-in h-full flex flex-col">
            <div className="flex justify-between items-center"><SectionTitle title="Profile & Tech Stack" /><GlassButton onClick={handleSaveProfile} className="bg-green-600 hover:bg-green-500 border-none min-w-[120px]">{isSaving ? <Loader2 className="animate-spin"/> : <><Save size={16} /> Save Changes</>}</GlassButton></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pb-10">
               <GlassCard className="p-6 space-y-6">
                  <div className="flex items-center gap-2 mb-2 border-b border-white/5 pb-2"><User size={18} className="text-blue-500"/><h3 className="text-white text-lg">Identity</h3></div>
                  <InputField label="Full Name" value={profile.name} onChange={(e: any) => setProfile({...profile, name: e.target.value})} />
                  <InputField label="Avatar URL (Image Link)" value={profile.avatarUrl} onChange={(e: any) => setProfile({...profile, avatarUrl: e.target.value})} placeholder="https://..." />
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="Nickname" value={profile.nickname} onChange={(e: any) => setProfile({...profile, nickname: e.target.value})} />
                    <InputField label="Location" value={profile.location} onChange={(e: any) => setProfile({...profile, location: e.target.value})} />
                  </div>
                  <InputField label="Email" value={profile.email} onChange={(e: any) => setProfile({...profile, email: e.target.value})} />
                  <TextAreaField label="Bio" value={profile.bio} onChange={(e: any) => setProfile({...profile, bio: e.target.value})} rows={6} />
               </GlassCard>
               <div className="space-y-8">
                  <GlassCard className="p-6 space-y-4"><div className="flex items-center gap-2 mb-2 border-b border-white/5 pb-2"><Layers size={18} className="text-purple-500" /><h3 className="text-white text-lg">Tech Stack</h3></div><div className="flex flex-wrap gap-2 mb-4">{profile.skills.map(skill => (<span key={skill} className="flex items-center gap-1 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-zinc-300">{skill}<button onClick={() => {const newSkills = profile.skills.filter(s=>s!==skill); setProfile({...profile, skills: newSkills})}} className="hover:text-white ml-1"><X size={12}/></button></span>))}</div><div className="flex gap-2"><input placeholder="Add skill..." value={skillInput} onChange={e => setSkillInput(e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded p-2 text-sm text-white" onKeyDown={(e) => {if(e.key==='Enter' && skillInput){ setProfile({...profile, skills: [...profile.skills, skillInput]}); setSkillInput('') }}} /><button onClick={()=>{if(skillInput){ setProfile({...profile, skills: [...profile.skills, skillInput]}); setSkillInput('') }}} className="bg-white/10 hover:bg-white/20 text-white p-2 rounded"><Plus size={18}/></button></div></GlassCard>
                  
                  {/* Experience Editor */}
                  <GlassCard className="p-6 space-y-4">
                      <div className="flex justify-between items-center border-b border-white/5 pb-2">
                         <div className="flex items-center gap-2"><Briefcase size={18} className="text-green-500"/><h3 className="text-white text-lg">Experience</h3></div>
                         <button onClick={addExperience} className="text-xs bg-white/10 px-2 py-1 rounded text-white hover:bg-white/20">+ Add</button>
                      </div>
                      <div className="space-y-4">
                         {profile.experience.map((exp, idx) => (
                            <div key={idx} className="bg-white/5 p-4 rounded-xl space-y-3 relative group">
                               <button onClick={() => removeExperience(idx)} className="absolute top-2 right-2 text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><X size={14}/></button>
                               <div className="grid grid-cols-2 gap-2">
                                  <input placeholder="Company" value={exp.company} onChange={e => updateExperience(idx, 'company', e.target.value)} className="bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                                  <input placeholder="Role" value={exp.role} onChange={e => updateExperience(idx, 'role', e.target.value)} className="bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                               </div>
                               <div className="grid grid-cols-2 gap-2">
                                  <input placeholder="Period" value={exp.period} onChange={e => updateExperience(idx, 'period', e.target.value)} className="bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                                  <input placeholder="Location" value={exp.location} onChange={e => updateExperience(idx, 'location', e.target.value)} className="bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                               </div>
                               <div className="grid grid-cols-2 gap-2">
                                  <input placeholder="Logo URL" value={exp.logoUrl} onChange={e => updateExperience(idx, 'logoUrl', e.target.value)} className="bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                                  <input placeholder="Website URL" value={(exp as any).website} onChange={e => updateExperience(idx, 'website', e.target.value)} className="bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                               </div>
                               <textarea placeholder="Description" rows={2} value={exp.description} onChange={e => updateExperience(idx, 'description', e.target.value)} className="w-full bg-black/20 p-2 rounded text-xs text-white border border-white/5 resize-none" />
                            </div>
                         ))}
                      </div>
                  </GlassCard>

                  {/* Education Editor */}
                  <GlassCard className="p-6 space-y-4">
                      <div className="flex justify-between items-center border-b border-white/5 pb-2">
                         <div className="flex items-center gap-2"><Building size={18} className="text-blue-400"/><h3 className="text-white text-lg">Education</h3></div>
                         <button onClick={addEducation} className="text-xs bg-white/10 px-2 py-1 rounded text-white hover:bg-white/20">+ Add</button>
                      </div>
                      <div className="space-y-4">
                         {profile.education.map((edu, idx) => (
                            <div key={idx} className="bg-white/5 p-4 rounded-xl space-y-3 relative group">
                               <button onClick={() => removeEducation(idx)} className="absolute top-2 right-2 text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><X size={14}/></button>
                               <div className="grid grid-cols-2 gap-2">
                                  <input placeholder="School" value={edu.school} onChange={e => updateEducation(idx, 'school', e.target.value)} className="bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                                  <input placeholder="Degree" value={edu.degree} onChange={e => updateEducation(idx, 'degree', e.target.value)} className="bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                               </div>
                               <div className="grid grid-cols-2 gap-2">
                                  <input placeholder="Field of Study" value={edu.fieldOfStudy || ''} onChange={e => updateEducation(idx, 'fieldOfStudy', e.target.value)} className="bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                                  <input placeholder="Grade" value={edu.grade || ''} onChange={e => updateEducation(idx, 'grade', e.target.value)} className="bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                               </div>
                               <div className="grid grid-cols-2 gap-2">
                                  <input placeholder="Year" value={edu.year} onChange={e => updateEducation(idx, 'year', e.target.value)} className="w-full bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                                  <input placeholder="Location" value={edu.location} onChange={e => updateEducation(idx, 'location', e.target.value)} className="w-full bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                               </div>
                               <div className="grid grid-cols-2 gap-2">
                                  <input placeholder="Logo URL" value={edu.logoUrl} onChange={e => updateEducation(idx, 'logoUrl', e.target.value)} className="bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                                  <input placeholder="Website URL" value={(edu as any).website} onChange={e => updateEducation(idx, 'website', e.target.value)} className="bg-black/20 p-2 rounded text-xs text-white border border-white/5" />
                               </div>
                            </div>
                         ))}
                      </div>
                  </GlassCard>

                  <GlassCard className="p-6 space-y-4"><div className="flex items-center gap-2 mb-2 border-b border-white/5 pb-2"><Share2 size={18} className="text-blue-400" /><h3 className="text-white text-lg">Socials</h3></div><InputField label="Calendly" value={profile.bookingUrl} onChange={(e: any) => setProfile({...profile, bookingUrl: e.target.value})} /><div className="grid grid-cols-2 gap-4"><InputField label="LinkedIn" value={profile.socials?.linkedin} onChange={(e: any) => setProfile({...profile, socials: {...profile.socials, linkedin: e.target.value}})} /><InputField label="X" value={profile.socials?.twitter} onChange={(e: any) => setProfile({...profile, socials: {...profile.socials, twitter: e.target.value}})} /><InputField label="GitHub" value={profile.socials?.github} onChange={(e: any) => setProfile({...profile, socials: {...profile.socials, github: e.target.value}})} /><InputField label="Telegram" value={profile.socials?.telegram} onChange={(e: any) => setProfile({...profile, socials: {...profile.socials, telegram: e.target.value}})} /></div></GlassCard>
               </div>
            </div>
          </div>
        )}

        {/* --- INBOX --- */}
        {activeView === 'inbox' && (
          <div className="space-y-6 animate-in fade-in">
              <SectionTitle title="Inbox" />
              <GlassCard className="p-0 overflow-hidden overflow-x-auto">
                 <table className="w-full text-left border-collapse min-w-[600px]">
                    <thead><tr className="bg-white/5 border-b border-white/10 text-xs text-zinc-500 uppercase"><th className="p-4">Date</th><th className="p-4">Sender</th><th className="p-4">Message</th><th className="p-4">Action</th></tr></thead>
                    <tbody>{messages.map(m => (<tr key={m.id} className={`border-b border-white/5 hover:bg-white/5 ${!m.read ? 'bg-blue-500/5' : ''}`}><td className="p-4 text-xs text-zinc-400">{new Date(m.date).toLocaleDateString()}</td><td className="p-4"><div className="text-white text-sm">{m.name}</div><div className="text-xs text-zinc-500">{m.email}</div></td><td className="p-4 text-sm text-zinc-300">{m.message}</td><td className="p-4">{!m.read && <button onClick={() => markMessageRead(m.id)} className="text-blue-400 text-xs hover:text-white">Mark Read</button>}</td></tr>))}</tbody>
                 </table>
              </GlassCard>
          </div>
        )}
      </div>
    </div>
  );
};
